import React, { useState } from 'react';
import { CheckIcon } from 'lucide-react';
import type { Product } from '../../types/product';
import { StarRating } from './StarRating';
import { reviews } from '../../data/filters';
import { formatNumber } from '../../utils/currency';
const tabs = [{
  id: 'overview',
  label: 'Overzicht'
}, {
  id: 'specs',
  label: 'Specificaties'
}, {
  id: 'reviews',
  label: 'Reviews'
}, {
  id: 'delivery',
  label: 'Levering & garantie'
}] as const;
type TabId = (typeof tabs)[number]['id'];
export function ProductTabs({
  product
}: {
  product: Product;
}) {
  const [active, setActive] = useState<TabId>('overview');
  return <section id="details" className="scroll-mt-40" aria-label="Productdetails" data-id="element-426" data-mp="346jrqlxzhfph">
      <div role="tablist" className="-mx-4 flex gap-6 overflow-x-auto border-b border-line px-4 sm:mx-0 sm:px-0" data-id="element-427" data-mp="3953c4hwxijfq">
        {tabs.map(t => <button key={t.id} role="tab" id={`tab-${t.id}`} aria-selected={active === t.id} aria-controls={`panel-${t.id}`} onClick={() => setActive(t.id)} className={`relative shrink-0 whitespace-nowrap py-3.5 text-sm font-semibold transition-colors duration-150 ${active === t.id ? 'text-ink after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-ink' : 'text-muted hover:text-ink'}`} data-id="element-428" data-mp="12bri9nzyxh69">
            {t.label}
            {t.id === 'reviews' && <span className="ml-1.5 font-medium text-muted" data-id="element-429" data-mp="2pawqxk6dvu1w">{formatNumber(product.reviews)}</span>}
          </button>)}
      </div>

      <div role="tabpanel" id={`panel-${active}`} aria-labelledby={`tab-${active}`} className="py-8" data-id="element-430" data-mp="2z8kmgcbtd5l4">
        {active === 'overview' && <div className="grid gap-8 lg:grid-cols-2" data-id="element-431" data-mp="2u8g40grci0s9">
            <p className="max-w-prose text-[15px] leading-relaxed text-ink/80" data-id="element-432" data-mp="eid4ciq165b0">{product.summary}</p>
            <ul className="space-y-3" data-id="element-433" data-mp="9j8xhyqkwjnu">
              {product.highlights.map(h => <li key={h} className="flex items-start gap-3 text-sm text-ink" data-id="element-434" data-mp="9lmp1xm1vdqn">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-volt" data-id="element-435" data-mp="hjooycygwqac">
                    <CheckIcon className="h-3 w-3 text-ink" strokeWidth={3} aria-hidden="true" data-id="element-436" data-mp="lne98q840lpk" />
                  </span>
                  {h}
                </li>)}
            </ul>
          </div>}

        {active === 'specs' && <dl className="max-w-2xl divide-y divide-line rounded-xl border border-line" data-id="element-437" data-mp="3u2eje57e99p0">
            {product.specTable.map(row => <div key={row.label} className="grid grid-cols-2 gap-4 px-4 py-3 text-sm" data-id="element-438" data-mp="2szc765cz34d">
                <dt className="text-muted" data-id="element-439" data-mp="7su1545kpims">{row.label}</dt>
                <dd className="font-medium text-ink" data-id="element-440" data-mp="cl4ybz8xiqbn">{row.value}</dd>
              </div>)}
          </dl>}

        {active === 'reviews' && <div className="grid gap-8 lg:grid-cols-12" data-id="element-441" data-mp="3g9yknglizxtw">
            <div className="lg:col-span-3" data-id="element-442" data-mp="3g8neja4lydzm">
              <p className="text-5xl font-bold tracking-tight text-ink" data-id="element-443" data-mp="38qfi5qwohbux">{formatNumber(product.rating)}</p>
              <div className="mt-2" data-id="element-444" data-mp="33rm49xdhbiu8">
                <StarRating rating={product.rating} size="md" data-id="element-445" data-mp="33snsz4nf43uf" />
              </div>
              <p className="mt-2 text-sm text-muted" data-id="element-446" data-mp="2yrgj901psul4">Op basis van {formatNumber(product.reviews)} geverifieerde reviews</p>
            </div>
            <ul className="divide-y divide-line lg:col-span-9" data-id="element-447" data-mp="2tswyh2yuxouc">
              {reviews.map(r => <li key={r.name} className="py-5 first:pt-0" data-id="element-448" data-mp="2pswb2sts63b5">
                  <div className="flex items-center justify-between gap-4" data-id="element-449" data-mp="2kztnd1bopo2v">
                    <StarRating rating={r.rating} data-id="element-450" data-mp="2g18lyykp4ycu" />
                    <span className="text-xs text-muted" data-id="element-451" data-mp="2g21iz7tus9hs">{r.date}</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-ink" data-id="element-452" data-mp="2b4i7gfojiatj">{r.text}</p>
                  <p className="mt-2 text-xs font-medium text-muted" data-id="element-453" data-mp="262aqfbous4kc">
                    {r.name} · {r.location} · Geverifieerde koper
                  </p>
                </li>)}
            </ul>
          </div>}

        {active === 'delivery' && <dl className="grid max-w-3xl gap-6 sm:grid-cols-3" data-id="element-454" data-mp="2vs9g3ygtcnav">
            <div data-id="element-455" data-mp="2vtkhjrmsdiyt">
              <dt className="text-sm font-semibold text-ink" data-id="element-456" data-mp="2i0l0vmb9pipo">Levering</dt>
              <dd className="mt-1 text-sm text-muted" data-id="element-457" data-mp="2hy8jpbdc3cdo">Gratis in NL &amp; BE vanaf € 50. Grote batterijen komen op een pallet met een tijdvak van 2 uur.</dd>
            </div>
            <div data-id="element-458" data-mp="280kflutu8siu">
              <dt className="text-sm font-semibold text-ink" data-id="element-459" data-mp="284894tza9exk">Retourneren</dt>
              <dd className="mt-1 text-sm text-muted" data-id="element-460" data-mp="2d2tbayy3i8on">30 dagen bedenktijd voor ongebruikte producten in de originele verpakking.</dd>
            </div>
            <div data-id="element-461" data-mp="232soh310pafp">
              <dt className="text-sm font-semibold text-ink" data-id="element-462" data-mp="232aj9gwdnlvn">Garantie</dt>
              <dd className="mt-1 text-sm text-muted" data-id="element-463" data-mp="3tfi2rwsjw2eg">Volledige fabrieksgarantie, door ons afgehandeld — je hoeft het merk niet zelf te benaderen.</dd>
            </div>
          </dl>}
      </div>
    </section>;
}